import type { ReactElement } from "react";

import type { ApiFailure } from "../../../types/errors";
import { useTranslation } from "../../i18n";
import { Button } from "../atoms/Button";
import { Heading } from "../atoms/Heading";
import { Icon } from "../atoms/Icon";
import type { HeadingLevel } from "../atoms/Heading";
import { USAGE_LIMIT_REACHED } from "./usageRefusal";
import { userMessage } from "./userMessage";
import styles from "./ErrorState.module.css";

export interface ErrorStateProps {
  /** An `ApiError` satisfies this: the class implements the interface. */
  readonly error: ApiFailure;
  readonly onRetry?: (() => void) | undefined;
  /** The heading's level: one under the region it replaces. `2` by default. */
  readonly headingLevel?: HeadingLevel;
  /** What failed, in the caller's words; the generic title when none is given. */
  readonly title?: string | undefined;
  /** `page`: the mockup's centred state for a whole screen; `inline` (default): the framed notice. */
  readonly layout?: "inline" | "page";
}

/**
 * What a container renders when its query fails.
 *
 * `role="alert"` because a failure that replaces content has to be announced,
 * and a retry action because an error with no way forward is a dead end: the
 * spec asks for the action, not only for the message.
 *
 * A usage refusal (UIX.2) is not a failure and trying again changes nothing: it
 * shows only the sentence of its reason, with no generic heading and no retry.
 */
export function ErrorState({ error, onRetry, headingLevel = 2, title, layout = "inline" }: ErrorStateProps): ReactElement {
  const { t } = useTranslation();
  const detail = userMessage(error, t("common.state.unknownError"));
  const usageRefused = error.code === USAGE_LIMIT_REACHED;

  return (
    <div className={styles.state} role="alert" data-layout={layout}>
      {layout === "page" ? (
        <span className={styles.icon}>
          <Icon name="alert-triangle" size="lg" />
        </span>
      ) : null}
      {usageRefused ? null : (
        <Heading level={headingLevel} className={styles.title}>
          {title ?? t("common.state.error")}
        </Heading>
      )}
      <p className={styles.detail}>{detail}</p>
      {onRetry === undefined || usageRefused ? null : (
        <Button variant={layout === "page" ? "primary" : "secondary"} onClick={onRetry}>
          {t("common.action.retry")}
        </Button>
      )}
    </div>
  );
}
